import * as React from 'react';
import { StoryblokComponent, storyblokEditable } from 'gatsby-source-storyblok';
import { Link } from 'gatsby';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon, library } from '@fortawesome/fontawesome-svg-core';
import { useLocation } from '@reach/router';
import * as icons from '../../icons/icon-pack';
import { extractLanguage } from '../../helpers/common';

const TILE_HEADER_BG = 'url(/assets/images/dark-green-bg.svg)';
const TILE_HEADER_BG_STYLE = {
  backgroundImage: TILE_HEADER_BG,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
};

function getTileSlug(tile) {
  return tile?.url?.story?.full_slug || '';
}

export default function ServicesTiles({ blok }) {
  const [expandedIndexes, setExpandedIndexes] = React.useState([0, 1]);
  library.add({ ...icons });

  const location = useLocation();
  const currentLanguage = extractLanguage(location.pathname);

  const tiles = blok?.tile || [];
  const learnMoreText = currentLanguage === 'en' ? 'Learn more' : 'Mehr erfahren';

  return (
    <div {...storyblokEditable(blok)} className="w-full px-4 lg:px-16">
      {blok?.blocks?.map((block) => (
        <StoryblokComponent
          blok={block}
          key={block._uid}
          className="m-auto w-full"
        />
      ))}

      {/* Desktop: 3-column card grid */}
      <div className="hidden lg:grid lg:grid-cols-3 gap-6">
        {tiles.map((item) => {
          const perkIcon = item?.icon ? icon({ prefix: 'far', iconName: item.icon }) : null;
          const description = item.description || '';
          const slug = getTileSlug(item);

          return (
            <div key={item._uid} className="flex flex-col overflow-hidden rounded-md border-2 border-latori-green-dark shadow-sm p-4">
              <div className="px-5 py-4 flex text-white gap-2 items-center" style={TILE_HEADER_BG_STYLE}>
                <div className="relative">
                  {perkIcon && (
                    <FontAwesomeIcon icon={perkIcon} className="flex-shrink-0 w-8 h-8" />
                  )}
                </div>
                <span className="font-semibold text-xl leading-tight">{item.title}</span>
              </div>
              <div className="flex flex-col flex-1 bg-white py-4">
                <p className="text-sm text-gray-600 leading-relaxed flex-1">{description}</p>
                {slug && (
                  <Link
                    to={`/${slug}`}
                    title={item?.url?.link_title || undefined}
                    className="mt-4 inline-block text-[19px]] text-latori-pink font-bold"
                  >
                    {learnMoreText}
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile: accordion */}
      <div className="lg:hidden flex flex-col gap-4">
        {tiles.map((item, index) => {
          const perkIcon = item?.icon ? icon({ prefix: 'far', iconName: item.icon }) : null;
          const description = item.description || '';
          const slug = getTileSlug(item);
          const isOpen = expandedIndexes.includes(index);

          return (
            <div key={item._uid} className=" rounded-md border-2 border-latori-green-dark-dark rounded-tl-none rounded-tr-none shadow-sm last:border-b-0">
              <button
                type="button"
                className="w-full text-left"
                onClick={() => {
                  setExpandedIndexes((prev) => (
                    prev.includes(index)
                      ? prev.filter((i) => i !== index)
                      : [...prev, index]
                  ));
                }}
                aria-expanded={isOpen}
              >
                <div className="text-white px-4 py-3 flex items-center justify-between" style={TILE_HEADER_BG_STYLE}>
                  <div className="flex items-center gap-3">
                    {perkIcon && <FontAwesomeIcon icon={perkIcon} className="flex-shrink-0" />}
                    <span className="font-semibold text-sm">{item.title}</span>
                  </div>
                  <span className="text-[10px] leading-none ml-3">{isOpen ? '▲' : '▼'}</span>
                </div>
                <div className="bg-background pb-2 px-4">
                  {/* <div className="h-[2px] w-10 bg-latori-pink" /> */}
                </div>
              </button>
              {isOpen && (
                <div className="bg-white px-4 py-4">
                  <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
                  {slug && (
                    <Link
                      to={`/${slug}`}
                      title={item?.url?.link_title || undefined}
                      className="mt-3 inline-block text-sm font-medium text-[#c40057] hover:underline"
                    >
                      {learnMoreText}
                    </Link>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {blok.cta_text && (
        <div className="flex justify-center">
          <Link to={currentLanguage === 'en' ? '/en/services' : '/services'} className="mt-10 text-xl button-primary">
            {blok.cta_text}
          </Link>
        </div>
      )}
    </div>
  );
}
